/**
 * <cart-cross-sell> — Productos complementarios en el carrito.
 *
 * Lee del endpoint nativo /recommendations/products.json?intent=complementary
 * que expone automáticamente el metafield
 * shopify--discovery--product_recommendation.complementary_products
 *
 * Producto base = último item agregado al carrito.
 * Si no hay complementarios → la sección queda oculta (hidden attribute).
 */

import { CartAddEvent, CartUpdateEvent } from '@theme/events';

const ENDPOINT = '/recommendations/products.json';

// Sticky state persists across re-renders of the section (the cart page section
// rebuilds <cart-cross-sell> on every cart update, losing instance state).
const STATE = (window.__cartCrossSellState = window.__cartCrossSellState || {
  baseId: null,    // kept for backward compat
  baseIds: null,   // all cart product IDs at the time recs were fetched
  products: null,
});
const CACHE = (window.__cartCrossSellCache = window.__cartCrossSellCache || new Map());

/**
 * Format price in cents to es-AR ARS string.
 * @param {number} cents
 */
function formatMoney(cents) {
  if (cents == null) return '';
  const amount = cents / 100;
  try {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'ARS',
      minimumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `$${amount.toFixed(2)}`;
  }
}

class CartCrossSell extends HTMLElement {
  /** @type {AbortController|null} */ #abort = null;
  /** @type {ResizeObserver|null} */ #resizeObs = null;
  /** @type {MutationObserver|null} */ #mutObs = null;
  #navHandler = null;
  #cartUpdateHandler = null;
  #restoreScheduled = false;

  connectedCallback() {
    this.limit = parseInt(this.dataset.limit || '4', 10);
    this.endpoint = this.dataset.recommendationsUrl || ENDPOINT;
    this.track = this.querySelector('[data-track]');
    this.template = this.querySelector('[data-card-template]');
    this.nav = this.querySelector('[data-nav]');

    // Refresh whenever cart updates (covers add, remove, change)
    this.#cartUpdateHandler = () => this.#refresh();
    document.addEventListener(CartAddEvent.eventName, this.#cartUpdateHandler);
    if (CartUpdateEvent && CartUpdateEvent.eventName) {
      document.addEventListener(CartUpdateEvent.eventName, this.#cartUpdateHandler);
    }
    document.addEventListener('cart:update', this.#cartUpdateHandler);

    this.#navHandler = (e) => {
      const btn = e.target.closest('[data-direction]');
      if (!btn || !this.track) return;
      const card = this.track.querySelector('.cart-cross-sell__card');
      const step = card ? card.getBoundingClientRect().width + 12 : 240;
      this.track.scrollBy({ left: btn.dataset.direction === 'next' ? step : -step, behavior: 'smooth' });
    };
    this.nav?.addEventListener('click', this.#navHandler);

    // After section morph the track may be wiped and `hidden` re-set from Liquid.
    // Watch for that and restore from sticky STATE without re-fetching.
    this.#mutObs = new MutationObserver(() => this.#scheduleRestore());
    this.#mutObs.observe(this, { attributes: true, attributeFilter: ['hidden'], childList: true, subtree: true });

    this.#refresh();
  }

  #scheduleRestore() {
    if (this.#restoreScheduled) return;
    this.#restoreScheduled = true;
    queueMicrotask(() => {
      this.#restoreScheduled = false;
      this.#restoreFromState();
    });
  }

  async #restoreFromState() {
    if (!STATE.baseId) return;
    // Re-resolve track/template in case section morph rebuilt internals
    this.track = this.querySelector('[data-track]');
    this.template = this.querySelector('[data-card-template]');
    this.nav = this.querySelector('[data-nav]');
    if (!this.track || !this.template) return;
    // Confirm sticky base is still in cart and itemCount < 3
    const { itemCount, productIds } = await this.#getCartState();
    if (itemCount >= 3) { this.hidden = true; return; }
    // Validate that at least one of the stored base products is still in the cart
    const validBaseIds = (STATE.baseIds || (STATE.baseId ? [STATE.baseId] : []))
      .filter((id) => productIds.includes(id));
    if (!validBaseIds.length) return;
    // Re-merge recommendations from cache for all still-present base products,
    // filtering against the current cart so freshly-added recs disappear.
    const seenIds = new Set();
    const filtered = [];
    for (const bid of validBaseIds) {
      for (const p of (CACHE.get(bid) || [])) {
        const sid = String(p.id);
        if (!seenIds.has(sid) && !productIds.includes(sid)) {
          seenIds.add(sid);
          filtered.push(p);
        }
      }
    }
    if (!filtered.length) return;
    STATE.products = filtered;
    if (this.track.children.length !== filtered.length) this.#render(filtered);
    if (this.hidden) this.hidden = false;
  }

  disconnectedCallback() {
    if (this.#cartUpdateHandler) {
      document.removeEventListener(CartAddEvent.eventName, this.#cartUpdateHandler);
      if (CartUpdateEvent?.eventName) document.removeEventListener(CartUpdateEvent.eventName, this.#cartUpdateHandler);
      document.removeEventListener('cart:update', this.#cartUpdateHandler);
    }
    this.nav?.removeEventListener('click', this.#navHandler);
    this.#abort?.abort();
    this.#resizeObs?.disconnect();
    this.#mutObs?.disconnect();
  }

  /** Returns { baseId, itemCount, productIds } based on current cart, baseId = most recent item product. */
  async #getCartState() {
    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!res.ok) return { baseId: null, itemCount: 0, productIds: [] };
      const cart = await res.json();
      if (!cart.items || !cart.items.length) return { baseId: null, itemCount: 0, productIds: [] };
      const last = cart.items[0];
      return {
        baseId: String(last.product_id),
        itemCount: cart.item_count || 0,
        productIds: cart.items.map((i) => String(i.product_id)),
      };
    } catch {
      return { baseId: null, itemCount: 0, productIds: [] };
    }
  }

  async #refresh() {
    const { baseId, itemCount, productIds } = await this.#getCartState();

    // Hide when cart empty — also reset sticky state.
    if (!baseId) {
      this.hidden = true;
      STATE.baseId = null;
      STATE.products = null;
      if (this.track) this.track.replaceChildren();
      return;
    }

    // Hide when cart already has 3+ items (space saving).
    if (itemCount >= 3) {
      this.hidden = true;
      return;
    }

    // If we have sticky base products still present in the cart, rebuild merged recs
    // from cache and re-render. This handles both page reloads and section morphs.
    const stickyBaseIds = (STATE.baseIds || (STATE.baseId ? [STATE.baseId] : []))
      .filter((id) => productIds.includes(id));
    if (stickyBaseIds.length) {
      const seenIds = new Set();
      const merged = [];
      for (const bid of stickyBaseIds) {
        for (const p of (CACHE.get(bid) || [])) {
          const sid = String(p.id);
          if (!seenIds.has(sid) && !productIds.includes(sid)) {
            seenIds.add(sid);
            merged.push(p);
          }
        }
      }
      if (merged.length) {
        STATE.products = merged;
        this.hidden = false;
        this.#render(merged);
        return;
      }
      // All merged recs already in cart — fall through to fetch fresh ones.
    }

    // No sticky state — fetch recommendations for ALL cart products and merge them.
    // This ensures the carousel shows complementary products for every item in the
    // cart, not just the first one that happens to have recommendations.
    this.dataset.loading = '';
    this.hidden = false;
    const seenIds = new Set();
    const products = [];
    for (const pid of productIds) {
      let recs = CACHE.get(pid);
      if (recs === undefined) {
        try {
          recs = await this.#fetchRecommendations(pid);
        } catch {
          recs = [];
        }
        CACHE.set(pid, recs);
      }
      for (const p of (recs || [])) {
        const sid = String(p.id);
        if (!seenIds.has(sid) && !productIds.includes(sid)) {
          seenIds.add(sid);
          products.push(p);
        }
      }
    }
    delete this.dataset.loading;

    if (!products.length) {
      this.hidden = true;
      return;
    }

    STATE.baseId = productIds[0]; // backward compat
    STATE.baseIds = [...productIds];
    STATE.products = products;
    this.hidden = false;
    this.#render(products);
  }

  async #fetchRecommendations(productId) {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const url = `${this.endpoint}?intent=complementary&product_id=${productId}&limit=${this.limit}`;
    const res = await fetch(url, { signal: this.#abort.signal, headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    return Array.isArray(data.products) ? data.products : [];
  }

  /** @param {any[]} products */
  #render(products) {
    if (!this.track || !this.template) return;
    this.track.replaceChildren();

    for (const product of products) {
      const node = /** @type {DocumentFragment} */ (this.template.content.cloneNode(true));
      const card = node.querySelector('.cart-cross-sell__card');
      const links = node.querySelectorAll('[data-link]');
      const img = node.querySelector('[data-image]');
      const titleEl = node.querySelector('[data-title]');
      const vendorEl = node.querySelector('[data-vendor]');
      const priceEl = node.querySelector('[data-price]');
      const compareEl = node.querySelector('[data-compare]');
      const badgeEl = node.querySelector('[data-badge]');
      const addBtn = node.querySelector('[data-add]');
      const addLabel = node.querySelector('[data-add-label]');
      const soldOut = node.querySelector('[data-sold-out]');

      links.forEach((a) => { a.setAttribute('href', product.url); });
      if (img) {
        img.src = (product.featured_image || '').replace(/\.(jpg|jpeg|png|webp)/i, '_400x.$1') || product.featured_image;
        img.alt = product.title || '';
      }
      if (titleEl) titleEl.textContent = product.title;
      if (vendorEl) vendorEl.textContent = product.vendor || '';
      if (priceEl) priceEl.textContent = formatMoney(product.price);

      const compare = product.compare_at_price || 0;
      if (compareEl && compare && compare > product.price) {
        compareEl.textContent = formatMoney(compare);
        compareEl.hidden = false;
        if (badgeEl) {
          const saving = compare - product.price;
          badgeEl.textContent = `-${Math.round((saving / compare) * 100)}%`;
          badgeEl.hidden = false;
        }
      }

      const allVariants = product.variants || [];
      const firstAvailable = allVariants.find((v) => v.available !== false) || allVariants[0];

      if (!product.available || !firstAvailable || allVariants.every((v) => v.available === false)) {
        if (soldOut) soldOut.hidden = false;
        if (addBtn) addBtn.disabled = true;
      } else {
        const vid = firstAvailable.id;
        addBtn?.addEventListener('click', () => this.#addToCart(addBtn, addLabel, vid));
      }

      this.track.appendChild(node);
    }

    // Show nav arrows only if scrollable; re-evaluate on resize and scroll
    const updateNav = () => {
      if (!this.nav || !this.track) return;
      const scrollable = this.track.scrollWidth > this.track.clientWidth + 4;
      this.nav.hidden = !scrollable || this.dataset.variant === 'compact';
    };
    requestAnimationFrame(updateNav);
    if (this.#resizeObs) this.#resizeObs.disconnect();
    if ('ResizeObserver' in window) {
      this.#resizeObs = new ResizeObserver(updateNav);
      this.#resizeObs.observe(this.track);
    }
  }

  /**
   * @param {HTMLButtonElement} btn
   * @param {Element|null} label
   * @param {string|number} variantId
   */
  async #addToCart(btn, label, variantId) {
    if (btn.disabled) return;
    const originalLabel = label?.textContent;
    btn.disabled = true;
    btn.dataset.state = 'loading';

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || `HTTP ${res.status}`);
      }

      btn.dataset.state = 'success';
      if (label) label.textContent = '✓ Agregado';

      // Notify the rest of the theme so cart drawer + bubble refresh
      const cart = await fetch('/cart.js').then((r) => r.json()).catch(() => null);
      document.dispatchEvent(new CartAddEvent(cart, 'cart-cross-sell', {
        source: 'cart-cross-sell',
        variantId: String(variantId),
        itemCount: cart?.item_count,
      }));

      setTimeout(() => {
        btn.dataset.state = '';
        btn.disabled = false;
        if (label && originalLabel) label.textContent = originalLabel;
      }, 1600);
    } catch (e) {
      console.error('[cart-cross-sell] add failed', e);
      btn.dataset.state = '';
      btn.disabled = false;
      if (label && originalLabel) label.textContent = originalLabel;
    }
  }
}

if (!customElements.get('cart-cross-sell')) {
  customElements.define('cart-cross-sell', CartCrossSell);
}
